import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Warning,
  OpenInNew,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '../../store/site.store';
import {
  getCannibalizationIssues,
  getCannibalizationSummary,
  QueryCannibalization,
} from '../../services/cannibalizationService';

const SeverityBadge: React.FC<{ score: number; isSevere: boolean }> = ({ score, isSevere }) => {
  const color = isSevere ? 'error' : score > 30 ? 'warning' : 'default';
  return (
    <Chip
      icon={isSevere ? <Warning fontSize="small" /> : undefined}
      label={isSevere ? `Severe · ${score?.toFixed(0)}` : `Score ${score?.toFixed(0)}`}
      size="small"
      color={color as 'error' | 'warning' | 'default'}
      variant="outlined"
    />
  );
};

const IssueRow: React.FC<{ issue: QueryCannibalization }> = ({ issue }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover sx={{ opacity: issue.isSevere ? 1 : 0.75 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <Typography variant="body2" fontWeight={600}>
              {issue.query}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip label={issue.pageCount} size="small" color={issue.pageCount >= 3 ? 'error' : 'warning'} />
        </TableCell>
        <TableCell align="right">{issue.totalImpressions?.toLocaleString()}</TableCell>
        <TableCell align="right">{issue.totalClicks?.toLocaleString()}</TableCell>
        <TableCell align="center">{issue.avgPosition?.toFixed(1)}</TableCell>
        <TableCell align="center">
          <SeverityBadge score={issue.severityScore} isSevere={issue.isSevere} />
        </TableCell>
        <TableCell>
          {issue.primaryPage && (
            <Tooltip title="Recommended page to keep">
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'success.main' }}>
                ✅ {issue.primaryPage}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {/* Expanded competing pages */}
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
                COMPETING PAGES
              </Typography>
              {issue.competingPages?.map((page, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', flex: 1 }}
                  >
                    {page === issue.primaryPage ? '✅' : '⚠️'} {page}
                  </Typography>
                  <IconButton size="small" onClick={() => window.open(page, '_blank')}>
                    <OpenInNew sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              {issue.redirectCandidates && issue.redirectCandidates.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                    SUGGESTED REDIRECTS
                  </Typography>
                  {issue.redirectCandidates.map((page, i) => (
                    <Typography key={i} variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'warning.main', display: 'block' }}>
                      → {page}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const CannibalizationDashboard: React.FC = () => {
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  const [severeOnly, setSevereOnly] = useState(false);

  const { data: summary } = useQuery({
    queryKey: ['cannibalization-summary', siteId],
    queryFn: () => getCannibalizationSummary(siteId!),
    enabled: !!siteId,
  });

  const { data: issues, isLoading, isError } = useQuery({
    queryKey: ['cannibalization', siteId, severeOnly],
    queryFn: () => getCannibalizationIssues(siteId!, severeOnly, 200),
    enabled: !!siteId,
  });

  if (!siteId) {
    return <Alert severity="info">Select a site to view cannibalization issues.</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          ⚔️ Query Cannibalization
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Queries where multiple pages compete against each other. Google can't decide which page
          to rank, so none reaches page 1. Fix: merge pages or canonicalize to a primary.
        </Typography>
      </Box>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Issues</Typography>
              <Typography variant="h4" fontWeight={700}>{summary?.totalIssues ?? '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Severe (3+ pages)</Typography>
              <Typography variant="h4" fontWeight={700} color="error.main">
                {summary?.severeIssues ?? '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch checked={severeOnly} onChange={(e) => setSevereOnly(e.target.checked)} color="error" />
          }
          label="Show severe issues only (3+ competing pages)"
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          Failed to load cannibalization data. Run the <strong>query-cannibalization</strong> batch job first.
        </Alert>
      ) : !issues?.length ? (
        <Alert severity="success">No cannibalization issues detected for this site. 🎉</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                <TableCell>Query</TableCell>
                <TableCell align="center">Pages</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="center">Avg Position</TableCell>
                <TableCell align="center">Severity</TableCell>
                <TableCell>Primary Page</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.map((issue: QueryCannibalization) => (
                <IssueRow key={issue.id} issue={issue} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CannibalizationDashboard;
